import moment from 'jalali-moment';
import { useEffect, useMemo, useState } from 'react';
import ReactPaginate from 'react-paginate';
import { useDispatch } from 'react-redux';
import endpoints from '../../config/endpoints';
import { setLoading } from '../../services/reducers/homeSlice';
import { middle } from '../../services/reducers/popupSlice';
import restApi from '../../services/restApi';
import { useAppSelector } from '../../services/store';
import { Sidebar } from '../Layout/Sidebar';
import InventoryAddStockModal from './InventoryAddStockModal';
import InventoryConvertModal from './InventoryConvertModal';
import CardexModal from './CardexModal';

type SortKey = 'sku' | 'title' | 'boxCount' | 'rollCount' | 'remains' | 'inventory';
type SortDirection = 'asc' | 'desc';
type Tab = 'inventory' | 'convert';

const itemsPerPage = 25;
const historyPerPage = 20;

const tabsTitles: Record<Tab, string> = {
  inventory: 'موجودی',
  convert: 'تبدیل رولی به کارتنی',
};

const sortIndicator = (active: boolean, direction: SortDirection) => {
  if (!active) return '';
  return direction === 'asc' ? ' ▲' : ' ▼';
};

const Inventory = () => {
  const dispatch = useDispatch();
  const userReducer = useAppSelector(state => state.userReducer);
  const [tab, setTab] = useState<Tab>('inventory');
  const [products, setProducts] = useState<any[]>([]);

  // Tab 1 — موجودی
  const [query, setQuery] = useState('');
  const [sortKey, setSortKey] = useState<SortKey>('title');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  const [itemOffset, setItemOffset] = useState(0);
  const [history, setHistory] = useState<any[]>([]);
  const [historyTotal, setHistoryTotal] = useState(0);
  const [historyPage, setHistoryPage] = useState(1);

  // Tab 2 — تبدیل رولی به کارتنی
  const [convertQuery, setConvertQuery] = useState('');
  const [convertOffset, setConvertOffset] = useState(0);

  const fetchProducts = async () => {
    dispatch(setLoading(true));
    const res = await restApi(endpoints.product.index, true).get();
    if (res.code === 200) {
      setProducts(res.data || []);
    }
    dispatch(setLoading(false));
  };

  const fetchHistory = async (page: number) => {
    const res = await restApi(endpoints.product.inventory + 'history', true).get({ page, limit: historyPerPage });
    if (res.code === 200) {
      setHistory(res.data?.logs || []);
      setHistoryTotal(res.data?.total || 0);
    }
  };

  useEffect(() => {
    fetchProducts();
    fetchHistory(1);
  }, []);

  const updateProduct = (updated: any) => {
    setProducts(prev => prev.map(p => (p.id === updated.id ? { ...p, ...updated } : p)));
    // The history log just gained a new row from this action — refresh page 1.
    setHistoryPage(1);
    fetchHistory(1);
  };

  const toggleSort = (key: SortKey) => {
    if (key === sortKey) {
      setSortDirection(prev => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDirection('asc');
    }
  };

  const roleScopedProducts = useMemo(() => {
    return products.filter(p => userReducer.profile?.role === 'FELTBOWLCOMMERCE' ? p.category?.slug === 'کاسه-نمد' : true);
  }, [products, userReducer.profile?.role]);

  const visibleProducts = useMemo(() => {
    return roleScopedProducts.filter(p =>
      p.sku?.toLowerCase().includes(query.toLowerCase()) ||
      p.title?.toLowerCase().includes(query.toLowerCase())
    );
  }, [roleScopedProducts, query]);

  const sortedProducts = useMemo(() => {
    const dir = sortDirection === 'asc' ? 1 : -1;
    return [...visibleProducts].sort((a, b) => {
      const aVal = a[sortKey];
      const bVal = b[sortKey];
      if (typeof aVal === 'string' || typeof bVal === 'string') {
        return dir * String(aVal || '').localeCompare(String(bVal || ''));
      }
      return dir * ((aVal || 0) - (bVal || 0));
    });
  }, [visibleProducts, sortKey, sortDirection]);

  const pageCount = Math.ceil(sortedProducts.length / itemsPerPage);
  const currentItems = sortedProducts.slice(itemOffset, itemOffset + itemsPerPage);
  const historyPageCount = Math.ceil(historyTotal / historyPerPage);

  const convertCandidates = useMemo(() => {
    return roleScopedProducts
      .filter(p => (p.rollCount || 0) > 0 && p.itemsPerRoll && p.itemsPerBox)
      .filter(p =>
        p.sku?.toLowerCase().includes(convertQuery.toLowerCase()) ||
        p.title?.toLowerCase().includes(convertQuery.toLowerCase())
      );
  }, [roleScopedProducts, convertQuery]);
  const convertPageCount = Math.ceil(convertCandidates.length / itemsPerPage);
  const currentConvertItems = convertCandidates.slice(convertOffset, convertOffset + itemsPerPage);

  const openAddStockModal = (product: any) => {
    dispatch(middle(<InventoryAddStockModal product={product} onSuccess={updateProduct} />));
  };

  const openConvertModal = (product: any) => {
    dispatch(middle(<InventoryConvertModal product={product} onSuccess={updateProduct} />));
  };

  const openCardexModal = (product: any) => {
    dispatch(middle(<CardexModal product={product} onRollback={updateProduct} />));
  };

  const th = (key: SortKey, label: string) => (
    <th className="clickable" onClick={() => toggleSort(key)}>
      {label}{sortIndicator(sortKey === key, sortDirection)}
    </th>
  );

  const unitLabel = (ut: string) => ut === 'box' ? 'تعداد کارتن قوطی' : 'تعداد کارتن رولی';

  return (
    <>
      <div className="dashboardBody">
        <Sidebar />
        <main className="dashBoardMain">
          <h1 className="dashBoardTitle">مدیریت انبار</h1>

          <div className="dashTabs inventory">
            {Object.entries(tabsTitles).map(([key, label]) => (
              <span
                key={key}
                className={`ordersTag inventory ${tab === key ? 'active' : ''}`}
                onClick={() => setTab(key as Tab)}
              >
                {label}
              </span>
            ))}
          </div>

          {tab === 'inventory' && (
            <>
              <div className="searchContainer">
                <div className="dashboardseaechBox">
                  <i className="dashMagnifierIcon"></i>
                  <input
                    className="dashSearchInput"
                    placeholder="جستجو بر اساس نام یا SKU"
                    onChange={(input) => setQuery(input.target.value)}
                  />
                </div>
              </div>

              <section className="shelf-management-section">
                <div className="shelf-management-header">
                  <h3 className="shelf-management-title">موجودی محصولات</h3>
                </div>

                <div className="cardex-table-container">
                  <table className="cardex-table">
                    <thead>
                    <tr className="cardex-table-header">
                      {th('sku', 'SKU')}
                      {th('title', 'نام محصول')}
                      {th('boxCount', 'تعداد کارتن قوطی')}
                      {th('rollCount', 'تعداد کارتن رولی')}
                      {th('remains', 'خرده تبدیل')}
                      {th('inventory', 'کل موجودی')}
                      <th>عملیات</th>
                    </tr>
                    </thead>
                    <tbody>
                    {currentItems.map((product) => (
                      <tr className="cardex-row" key={product.id}>
                        <td className="cardex-cell">{product.sku}</td>
                        <td className="cardex-cell">{product.title}</td>
                        <td className="cardex-cell">
                          <span className="cardex-count">{product.boxCount || 0} × {product.itemsPerBox || '—'}</span>
                        </td>
                        <td className="cardex-cell">
                          <span className="cardex-count">{product.rollCount || 0} × {product.itemsPerRoll || '—'}</span>
                        </td>
                        <td className="cardex-cell">{product.remains || 0}</td>
                        <td className="cardex-cell">
                          <span className={`cardex-count ${
                            product.inventory > 0 ? 'cardex-stock-positive' :
                            product.inventory === 0 ? 'cardex-stock-zero' : 'cardex-stock-negative'
                          }`}>
                            {product.inventory || 0}
                          </span>
                        </td>
                        <td className="cardex-cell" style={{ display: 'flex', gap: '8px' }}>
                          <span className="dashboardHeader clickable" onClick={() => openAddStockModal(product)}>
                            <p>افزایش موجودی</p>
                          </span>
                          <span className="secondaryBtn clickable" onClick={() => openCardexModal(product)}>
                            <p>کاردکس</p>
                          </span>
                        </td>
                      </tr>
                    ))}
                    {currentItems.length === 0 && (
                      <tr>
                        <td className="cardex-cell" colSpan={7}>محصولی یافت نشد</td>
                      </tr>
                    )}
                    </tbody>
                  </table>
                </div>

                <ReactPaginate
                  breakLabel="..."
                  nextLabel="بعدی >"
                  onPageChange={(event) => setItemOffset((event.selected * itemsPerPage) % (sortedProducts.length || 1))}
                  pageRangeDisplayed={5}
                  pageCount={pageCount}
                  previousLabel="< قبلی"
                  renderOnZeroPageCount={null}
                  className="pagination"
                  pageClassName="paginationBreak"
                  previousClassName="paginationBreak"
                  nextClassName="paginationBreak"
                  activeClassName="paginationActive"
                />
              </section>

              <section className="shelf-management-section">
                <div className="shelf-management-header">
                  <h3 className="shelf-management-title">تاریخچه افزایش موجودی</h3>
                </div>

                <div className="cardex-table-container">
                  <table className="cardex-table">
                    <thead>
                    <tr className="cardex-table-header">
                      <th>تاریخ</th>
                      <th>محصول</th>
                      <th>نوع</th>
                      <th>تعداد</th>
                      <th>موجودی قبل</th>
                      <th>موجودی بعد</th>
                    </tr>
                    </thead>
                    <tbody>
                    {history.map((log) => (
                      <tr className="cardex-row" key={log.id}>
                        <td className="cardex-cell">{moment(log.createdAt).format('jYYYY/jMM/jDD HH:mm')}</td>
                        <td className="cardex-cell">{log.product?.title} ({log.product?.sku})</td>
                        <td className="cardex-cell">{unitLabel(log.unitType)}</td>
                        <td className="cardex-cell">{log.count} × {log.itemsPerUnit || '—'}</td>
                        <td className="cardex-cell">{log.oldInventory ?? '—'}</td>
                        <td className="cardex-cell">{log.newInventory ?? '—'}</td>
                      </tr>
                    ))}
                    {history.length === 0 && (
                      <tr>
                        <td className="cardex-cell" colSpan={6}>سابقه‌ای یافت نشد</td>
                      </tr>
                    )}
                    </tbody>
                  </table>
                </div>

                <ReactPaginate
                  breakLabel="..."
                  nextLabel="بعدی >"
                  onPageChange={(event) => {
                    const nextPage = event.selected + 1;
                    setHistoryPage(nextPage);
                    fetchHistory(nextPage);
                  }}
                  pageRangeDisplayed={5}
                  pageCount={historyPageCount}
                  forcePage={historyPage - 1}
                  previousLabel="< قبلی"
                  renderOnZeroPageCount={null}
                  className="pagination"
                  pageClassName="paginationBreak"
                  previousClassName="paginationBreak"
                  nextClassName="paginationBreak"
                  activeClassName="paginationActive"
                />
              </section>
            </>
          )}

          {tab === 'convert' && (
            <>
              <div className="searchContainer">
                <div className="dashboardseaechBox">
                  <i className="dashMagnifierIcon"></i>
                  <input
                    className="dashSearchInput"
                    placeholder="جستجو بر اساس نام یا SKU"
                    onChange={(input) => setConvertQuery(input.target.value)}
                  />
                </div>
              </div>

              <section className="shelf-management-section">
                <div className="shelf-management-header">
                  <h3 className="shelf-management-title">تبدیل رولی به کارتنی</h3>
                </div>

                <div className="cardex-table-container">
                  <table className="cardex-table">
                    <thead>
                    <tr className="cardex-table-header">
                      <th>SKU</th>
                      <th>نام محصول</th>
                      <th>تعداد کارتن رولی</th>
                      <th>تعداد کارتن قوطی</th>
                      <th>خرده تبدیل</th>
                      <th>عملیات</th>
                    </tr>
                    </thead>
                    <tbody>
                    {currentConvertItems.map((product) => (
                      <tr className="cardex-row" key={product.id}>
                        <td className="cardex-cell">{product.sku}</td>
                        <td className="cardex-cell">{product.title}</td>
                        <td className="cardex-cell">{product.rollCount || 0} × {product.itemsPerRoll || '—'}</td>
                        <td className="cardex-cell">{product.boxCount || 0} × {product.itemsPerBox || '—'}</td>
                        <td className="cardex-cell">{product.remains || 0}</td>
                        <td className="cardex-cell">
                          <span className="dashboardHeader clickable" onClick={() => openConvertModal(product)}>
                            <p>تبدیل</p>
                          </span>
                        </td>
                      </tr>
                    ))}
                    {currentConvertItems.length === 0 && (
                      <tr>
                        <td className="cardex-cell" colSpan={6}>محصولی با موجودی رول یافت نشد</td>
                      </tr>
                    )}
                    </tbody>
                  </table>
                </div>

                <ReactPaginate
                  breakLabel="..."
                  nextLabel="بعدی >"
                  onPageChange={(event) => setConvertOffset((event.selected * itemsPerPage) % (convertCandidates.length || 1))}
                  pageRangeDisplayed={5}
                  pageCount={convertPageCount}
                  previousLabel="< قبلی"
                  renderOnZeroPageCount={null}
                  className="pagination"
                  pageClassName="paginationBreak"
                  previousClassName="paginationBreak"
                  nextClassName="paginationBreak"
                  activeClassName="paginationActive"
                />
              </section>
            </>
          )}
        </main>
      </div>
    </>
  );
};

export default Inventory;
